import queryString from 'query-string';
import { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { ChartFragment, Section } from 'models/api-response';
import { FormFieldInputType, FragmentType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps } from 'models/ui';
import { AppMessage, NAV_ID, RouteParams, RoutePath } from 'constants/app.constant';
import { appHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { FormField, FormFieldLabel, SectionTitle } from './constants';
import OstomiesView from './OstomiesView';

interface OstomiesProps extends RouteComponentProps {
  showConfirmationModal: Function;
  enableMultiStepsAuthoring: Function;
}
interface OstomiesState {
  loadedFragment: ChartFragment;
  fragments: ChartFragment[];
}

class Ostomies extends Component<OstomiesProps & ChartComponentProps, OstomiesState> {
  static displayName = 'Ostomies';

  constructor(props) {
    super(props);
    this.state = {
      loadedFragment: null,
      fragments: null
    };
  }

  componentDidMount() {
    const id = this.getIdFromParam(this.props);
    this.loadData(id);
    this.props.enableMultiStepsAuthoring(true);
  }

  componentDidUpdate(prevProps) {
    const prevId = this.getIdFromParam(prevProps);
    const id = this.getIdFromParam(this.props);
    // Prepare for creating a new Ostomy entry when re-click on the left navigation
    if (id !== prevId && !id) {
      this.setState({ loadedFragment: null });
      this.props.initState(this.buildFormFields());
    }
  }

  isDiscontinuedExisted = (fragments: ChartFragment[], fragment: ChartFragment) => {
    if (!fragment) return false;
    const fragmentList = fragments.filter((frag) => frag.linkedFragmentId === (fragment.linkedFragmentId || fragment.fragmentId));
    return fragmentList.some((frag) => frag.active && chartHelper.getFragmentValue(frag, SectionTitle.OSTOMIES, FormField.DISCONTINUED));
  };

  loadData = (id) =>
    appHelper.useLoader(
      this.props.loadChartData([FragmentType.AUTHORED, FragmentType.CHARTING]).then(({ data: fragments }) => {
        let chartFragment = fragments.find((fragment) => fragment.fragmentId === id && fragment.active);
        if (this.isDiscontinuedExisted(fragments, chartFragment)) {
          chartFragment = null;
          const ostomiesEntryPage = RoutePath.student.ostomies.ostomies.replace(RouteParams.ASSESSMENT_ID, String(this.props.assessment.eolsAssessmentId));
          this.props.history.push(ostomiesEntryPage);
        }
        this.setState({ loadedFragment: chartFragment, fragments });
        this.props.initState(this.buildFormFields(chartFragment));
      }),
      { errorMessage: AppMessage.NO_CHART_DATA }
    );

  getIdFromParam = (props: OstomiesProps) => {
    const parsedQuery = queryString.parse(props.location.search);
    return parsedQuery.id;
  };

  buildFormFields = (fragment?: ChartFragment) => {
    const { createFormField, getFragmentContentIds, getFragmentValue } = chartHelper;
    const dataMap = new Map();

    const dropdowns = [
      { name: FormField.LOCATION, sectionTitle: SectionTitle.OSTOMIES, label: FormFieldLabel.LOCATION },
      { name: FormField.OSTOMY_TYPE, sectionTitle: SectionTitle.OSTOMIES, label: FormFieldLabel.OSTOMY_TYPE }
    ];

    const multiselectDropdowns = [
      { name: FormField.PERI_STOMA_PERI_INSERTION, sectionTitle: SectionTitle.PERI_STOMA_PERI_INSERTION },
      { name: FormField.STOMA_CONDITION, sectionTitle: SectionTitle.STOMA_CONDITION },
      { name: FormField.DRAINAGE_FROM_OSTOMY, sectionTitle: SectionTitle.DRAINAGE_FROM_OSTOMY },
      { name: FormField.OSTOMY_PATENCY, sectionTitle: SectionTitle.OSTOMY_PATENCY },
      { name: FormField.PATIENT_RESPONSE, sectionTitle: SectionTitle.PATIENT_RESPONSE },
      { name: FormField.DRESSING_AND_OSTOMY_CARE, sectionTitle: SectionTitle.DRESSING_AND_OSTOMY_CARE }
    ];

    dataMap.set(
      FormField.DISCONTINUED,
      createFormField({
        name: FormField.DISCONTINUED,
        type: FormFieldInputType.CHECK_BOX,
        contentIds: getFragmentContentIds(fragment, SectionTitle.OSTOMIES, FormField.DISCONTINUED)
      })
    );

    dataMap.set(
      FormField.NOTES,
      createFormField({
        name: FormField.NOTES,
        type: FormFieldInputType.TEXT_AREA,
        value: getFragmentValue(fragment, SectionTitle.NOTES, FormField.NOTES)
      })
    );

    dropdowns.forEach(({ name, sectionTitle, label }) =>
      dataMap.set(
        name,
        createFormField({
          name,
          label,
          type: FormFieldInputType.DROPDOWN,
          contentIds: getFragmentContentIds(fragment, sectionTitle, name)
        })
      )
    );

    multiselectDropdowns.forEach(({ name, sectionTitle }) =>
      dataMap.set(
        name,
        createFormField({
          name,
          type: FormFieldInputType.MULTISELECT_DROPDOWN,
          contentIds: getFragmentContentIds(fragment, sectionTitle, name)
        })
      )
    );
    return dataMap;
  };

  createSections = (): Section[] => {
    const { formFieldMap } = this.props;
    const { buildSection } = chartHelper;
    return [
      buildSection({
        sectionTitle: SectionTitle.OSTOMIES,
        fields: [FormField.DISCONTINUED, FormField.LOCATION, FormField.OSTOMY_TYPE],
        formFieldMap
      }),
      buildSection({ sectionTitle: SectionTitle.PERI_STOMA_PERI_INSERTION, fields: [FormField.PERI_STOMA_PERI_INSERTION], formFieldMap }),
      buildSection({ sectionTitle: SectionTitle.STOMA_CONDITION, fields: [FormField.STOMA_CONDITION], formFieldMap }),
      buildSection({ sectionTitle: SectionTitle.DRAINAGE_FROM_OSTOMY, fields: [FormField.DRAINAGE_FROM_OSTOMY], formFieldMap }),
      buildSection({ sectionTitle: SectionTitle.OSTOMY_PATENCY, fields: [FormField.OSTOMY_PATENCY], formFieldMap }),
      buildSection({ sectionTitle: SectionTitle.PATIENT_RESPONSE, fields: [FormField.PATIENT_RESPONSE], formFieldMap }),
      buildSection({ sectionTitle: SectionTitle.DRESSING_AND_OSTOMY_CARE, fields: [FormField.DRESSING_AND_OSTOMY_CARE], formFieldMap }),
      buildSection({ sectionTitle: SectionTitle.NOTES, fields: [FormField.NOTES], formFieldMap })
    ];
  };

  buildFragment = () => {
    const { isAuthor } = this.props;
    const record = {
      chartTitle: SectionTitle.OSTOMIES,
      fragmentTitle: SectionTitle.OSTOMIES,
      records: this.createSections()
    };
    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const fragmentId = chartHelper.createFragmentId(NAV_ID.OSTOMIES);
    const basicInfo = chartService.createBaseFragment({ fragmentId, chartingTime: this.props.chartingTime });
    let linkedFragmentId;
    const { loadedFragment } = this.state;
    if (!loadedFragment) {
      linkedFragmentId = fragmentId;
    } else {
      linkedFragmentId = loadedFragment.linkedFragmentId || loadedFragment.fragmentId;
    }
    return { ...basicInfo, linkedFragmentId, groupFragmentRef: isAuthor ? linkedFragmentId : undefined, chartData: cleanRecord } as ChartFragment;
  };

  saveChart = () => {
    const fragment = this.buildFragment();
    const defaultFormFields = this.buildFormFields(this.state.loadedFragment);
    return this.props.saveChart([fragment], { defaultFormFields, afterSave: this.props.backToSourceLocation || this.props.navigateToSavedPatientCharting });
  };

  handleSaveClick = () => {
    const { loadedFragment, fragments } = this.state;
    const { formFieldMap } = this.props;
    const curLocation = formFieldMap.get(FormField.LOCATION).value;
    const isSavingActive = !formFieldMap.get(FormField.DISCONTINUED).value;

    const needShowWarning = isSavingActive && !loadedFragment && this.isLocationOfActiveExisted(fragments, curLocation);
    if (needShowWarning) {
      this.props.showConfirmationModal({
        showIcon: true,
        header: 'Please confirm.',
        message: 'You already have an active Ostomies at this location. Are you sure you want to add another?',
        onOkClick: this.saveChart
      });
    } else {
      this.saveChart();
    }
  };

  isLocationOfActiveExisted = (fragments, newLocation: string) => {
    const fragmentList = fragments.filter((frag) => {
      const location = chartHelper.getFragmentValue(frag, SectionTitle.OSTOMIES, FormField.LOCATION);
      return frag.active && location === newLocation;
    });

    let result = false;
    const fragmentGroups = chartHelper.groupFragmentsById(fragmentList);
    fragmentGroups.forEach((fragmentGroup) => {
      const isDiscontinued = fragmentGroup.some((frag) => chartHelper.getFragmentValue(frag, SectionTitle.OSTOMIES, FormField.DISCONTINUED));
      if (!isDiscontinued) {
        result = true;
      }
    });

    return result;
  };

  handleCancelClick = () => this.props.handleDiscardClick(undefined, this.buildFormFields(this.state.loadedFragment));

  render() {
    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton: this.props.enableDisplayRecordsButton,
      enableSaveButton: this.props.hasUnsavedChanges,
      onSaveClick: this.handleSaveClick,
      onCancelClick: this.handleCancelClick,
      onDisplayRecordsClick: this.props.displayAuthoringData
    };

    const viewProps = {
      fragment: this.state.loadedFragment,
      chartActionsProps,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount
    };
    return <OstomiesView {...viewProps} />;
  }
}

export { Ostomies as BaseOstomies };
export default compose(withRouter, withChartLogic, withSavedPatientChartsPage)(Ostomies);
